"use client";

import React, { useState, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import SingleEntityPickerModal from "@/components/SingleEntityPickerModal/SingleEntityPickerModal";
import {
  BaseEntity,
  FetchEntitiesFn,
  CreateEntityFn,
} from "@/components/EntityPickerModal/EntityPickerModal";

interface ChipSingleSelectProps<T extends BaseEntity> {
  value: T | null;
  onChange: (newValue: T | null) => void;
  fetchEntities: FetchEntitiesFn<T>;
  createEntity?: CreateEntityFn<T>;
  entityNameSingular?: string;
  entityNamePlural?: string;
  label: string;
  renderEntityRow?: (
    entity: T,
    isSelected: boolean,
    toggle: (ent: T) => void,
  ) => React.ReactNode;
}

export function ChipSingleSelect<T extends BaseEntity>({
  value,
  onChange,
  fetchEntities,
  createEntity,
  entityNameSingular,
  entityNamePlural,
  label,
  renderEntityRow,
}: ChipSingleSelectProps<T>) {
  const [selectedEntity, setSelectedEntity] = useState<T | null>(value);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setSelectedEntity(value);
  }, [value]);

  const handleConfirm = (newSelectedEntity: T | null) => {
    setSelectedEntity(newSelectedEntity);
    onChange(newSelectedEntity);
    setModalOpen(false);
  };

  return (
    <>
      <button
        className="inline-flex items-center justify-between w-full sm:w-auto sm:justify-start gap-2 h-9 px-3 py-1 rounded-md border shadow-sm text-sm font-medium transition-colors bg-white dark:bg-gray-800 border-gray-200 dark:border-white/20 text-gray-600 dark:text-white hover:border-gray-300 dark:hover:border-white/30 hover:bg-gray-50 dark:hover:bg-gray-700"
        onClick={() => setModalOpen(true)}
        type="button"
      >
        <span>{label}</span>
        <span className="inline-flex items-center gap-1">
          {selectedEntity && (
            <span className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full bg-primary">
              <Check className="h-3 w-3 text-white" />
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </span>
      </button>

      <SingleEntityPickerModal<T>
        createEntity={createEntity}
        entityNamePlural={entityNamePlural}
        entityNameSingular={entityNameSingular}
        fetchEntities={fetchEntities}
        initialSelectedEntity={selectedEntity}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        renderEntityRow={renderEntityRow}
      />
    </>
  );
}
